import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Camera, Wand2, Loader2, Download, Trash2, Image as ImageIcon } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const ROOMS = [
  { key: 'oceans13', name: 'Oceans 13 — Bank Heist' },
  { key: 'demolition', name: 'Demolition Abduction' },
  { key: 'pirate', name: 'Pirate Ship' },
  { key: 'sweet_escape', name: 'Bake Me Happy — Kitchen Escape' },
];

const FONTS = [
  { key: 'bold', name: 'Bold Sans (Default)' },
  { key: 'pirate', name: 'Pirate — Pirata One' },
  { key: 'horror', name: 'Horror — Creepster' },
  { key: 'marker', name: 'Marker — Permanent Marker' },
  { key: 'comic', name: 'Comic Action — Bangers' },
  { key: 'scifi', name: 'Sci-Fi — Audiowide' },
  { key: 'western', name: 'Western — Rye' },
  { key: 'script', name: 'Elegant Script — Great Vibes' },
  { key: 'arcade', name: 'Retro Arcade — Press Start 2P' },
  { key: 'typewriter', name: 'Typewriter — Special Elite' },
];

export default function PhotoBrander() {
  const [photo, setPhoto] = useState(null); // base64
  const [preview, setPreview] = useState(null); // data URL for preview
  const [room, setRoom] = useState('oceans13');
  const [customText, setCustomText] = useState('');
  const [escaped, setEscaped] = useState(true);
  const [font, setFont] = useState('bold');
  const [generating, setGenerating] = useState(false);
  const [result, setResult] = useState(null); // base64
  const [error, setError] = useState('');
  const [history, setHistory] = useState([]);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/api/admin/branded-photos`, { withCredentials: true })
      .then(res => setHistory(res.data))
      .catch(() => {});
  }, []);

  const onFile = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      setPreview(dataUrl);
      setPhoto(dataUrl.split(',')[1]); // strip data:image/...;base64,
      setResult(null);
      setError('');
    };
    reader.readAsDataURL(file);
  };

  const generate = async () => {
    if (!photo) return;
    setGenerating(true);
    setError('');
    setResult(null);
    try {
      const { data } = await axios.post(`${API}/api/admin/brand-photo`, {
        photo_base64: photo,
        room,
        custom_text: customText.trim(),
        escaped,
        font,
      }, { withCredentials: true });
      setResult(data.image_base64);
      setHistory(prev => [{ id: data.id, room, image_base64: data.image_base64, created_at: new Date().toISOString() }, ...prev]);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to generate branded image');
    }
    setGenerating(false);
  };

  const downloadImage = (b64, filename) => {
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${b64}`;
    link.download = filename || 'okiescape-branded.png';
    link.click();
  };

  const deletePhoto = async (id) => {
    await axios.delete(`${API}/api/admin/branded-photos/${id}`, { withCredentials: true });
    setHistory(prev => prev.filter(h => h.id !== id));
  };

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8 space-y-6">
      <div>
        <h2 className="font-heading text-2xl tracking-wider uppercase text-foreground mb-1">Photo Branding</h2>
        <p className="font-sans text-sm text-muted-foreground">Upload a customer photo and AI will create a branded escape room image with your logo, room theme, and the year.</p>
      </div>

      {/* Upload & Config */}
      <div className="border border-border rounded-xl bg-card p-5 space-y-4" data-testid="photo-brand-form">
        <div className="grid sm:grid-cols-2 gap-4">
          {/* Photo upload */}
          <div>
            <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-2 block">Customer Photo</label>
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="photo-upload-input" />
            {preview ? (
              <div className="relative group">
                <img src={preview} alt="Customer" className="w-full h-48 object-cover rounded-xl border border-border" />
                <button
                  onClick={() => fileRef.current?.click()}
                  className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity rounded-xl flex items-center justify-center font-heading text-xs tracking-widest uppercase text-white"
                >
                  Change Photo
                </button>
              </div>
            ) : (
              <button
                onClick={() => fileRef.current?.click()}
                data-testid="photo-upload-btn"
                className="w-full h-48 border-2 border-dashed border-border rounded-xl flex flex-col items-center justify-center gap-2 hover:border-brand/50 hover:bg-brand/5 transition-colors"
              >
                <Camera size={28} className="text-muted-foreground" />
                <span className="font-heading text-xs tracking-widest uppercase text-muted-foreground">Upload Photo</span>
              </button>
            )}
          </div>

          {/* Room & options */}
          <div className="space-y-3">
            <div>
              <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-1 block">Room Theme</label>
              <select
                value={room}
                onChange={e => setRoom(e.target.value)}
                data-testid="room-select"
                className="w-full bg-background border border-border rounded-xl px-4 py-3 font-sans text-sm text-foreground focus:outline-none focus:border-brand"
              >
                {ROOMS.map(r => (
                  <option key={r.key} value={r.key}>{r.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-1 block">Font Style</label>
              <select
                value={font}
                onChange={e => setFont(e.target.value)}
                data-testid="font-select"
                className="w-full bg-background border border-border rounded-xl px-4 py-3 font-sans text-sm text-foreground focus:outline-none focus:border-brand"
              >
                {FONTS.map(f => (
                  <option key={f.key} value={f.key}>{f.name}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-1 block">Custom Text (Optional)</label>
              <input
                value={customText}
                onChange={e => setCustomText(e.target.value)}
                placeholder='e.g. "Happy Birthday Sarah!"'
                data-testid="custom-text-input"
                className="w-full bg-background border border-border rounded-xl px-4 py-3 font-sans text-sm text-foreground focus:outline-none focus:border-brand placeholder-muted-foreground"
              />
            </div>
            <div>
              <label className="font-heading text-[11px] tracking-widest uppercase text-muted-foreground mb-1 block">Did they escape?</label>
              <div className="flex gap-2">
                <button onClick={() => setEscaped(true)} data-testid="escaped-yes" className={`flex-1 font-heading text-sm tracking-widest uppercase py-2.5 rounded-xl border-2 transition-colors ${escaped ? 'border-green-500 bg-green-500/10 text-green-400' : 'border-border text-muted-foreground'}`}>
                  WE ESCAPED
                </button>
                <button onClick={() => setEscaped(false)} data-testid="escaped-no" className={`flex-1 font-heading text-sm tracking-widest uppercase py-2.5 rounded-xl border-2 transition-colors ${!escaped ? 'border-brand bg-brand/10 text-brand' : 'border-border text-muted-foreground'}`}>
                  SO CLOSE
                </button>
              </div>
            </div>
            <button
              onClick={generate}
              disabled={!photo || generating}
              data-testid="generate-brand-btn"
              className="w-full flex items-center justify-center gap-2 font-heading text-sm tracking-widest uppercase bg-brand text-white py-4 rounded-xl hover:bg-brand-hover transition-colors disabled:opacity-40"
            >
              {generating ? <><Loader2 size={16} className="animate-spin" /> Creating Branded Image...</> : <><Wand2 size={16} /> Generate Branded Photo</>}
            </button>
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="border border-red-500/40 bg-red-900/10 rounded-xl p-3 text-sm text-red-400 font-sans">{error}</div>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-3" data-testid="brand-result">
            <div className="border border-brand/30 rounded-xl overflow-hidden">
              <img src={`data:image/png;base64,${result}`} alt="Branded" className="w-full" />
            </div>
            <button
              onClick={() => downloadImage(result, `okiescape-${room}-${Date.now()}.png`)}
              data-testid="download-brand-btn"
              className="flex items-center gap-2 font-heading text-xs tracking-widest uppercase bg-brand text-white px-5 py-3 rounded-xl hover:bg-brand-hover transition-colors"
            >
              <Download size={14} /> Download Image
            </button>
          </div>
        )}
      </div>

      {/* History */}
      {history.length > 0 && (
        <div className="border border-border rounded-xl bg-card p-5" data-testid="brand-history">
          <div className="flex items-center gap-2 mb-4">
            <ImageIcon size={16} className="text-brand" />
            <h3 className="font-heading text-sm tracking-widest uppercase text-foreground">Recent Branded Photos</h3>
            <span className="font-sans text-xs text-muted-foreground">{history.length}</span>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {history.map(h => (
              <div key={h.id} className="relative group rounded-xl overflow-hidden border border-border">
                <img src={`data:image/png;base64,${h.image_base64}`} alt="Branded" className="w-full aspect-square object-cover" />
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                  <button onClick={() => downloadImage(h.image_base64, `okiescape-${h.room}.png`)} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-white/40 transition-colors">
                    <Download size={14} />
                  </button>
                  <button onClick={() => deletePhoto(h.id)} className="w-8 h-8 rounded-full bg-white/20 flex items-center justify-center text-white hover:bg-red-500/60 transition-colors">
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="absolute bottom-0 left-0 right-0 bg-black/70 px-2 py-1">
                  <p className="font-heading text-[9px] tracking-widest uppercase text-neutral-300 truncate">{ROOMS.find(r => r.key === h.room)?.name || h.room}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
